import type { Dispatch } from 'react';

import { EmergencySituationInstructions, type AttachmentBody } from '../realtime';
import type { IncidentChatAction } from './incidentChatState';
import type { IncidentRealtime } from './incidentRealtime';

export type IncidentChatActions = {
  sendMessage(message: string): Promise<void>;
  sendImageMessage(attachment: AttachmentBody): Promise<void>;
  sendMessagePreview(message: string): Promise<void>;
};

export function useIncidentChatActions(
  realtime: IncidentRealtime | null,
  dispatch: Dispatch<IncidentChatAction>,
): IncidentChatActions {
  const sendMessage = async (message: string) => {
    await realtime?.sendChatMessage({
      message,
      emergencySituationInstructions: EmergencySituationInstructions.None,
      attachment: null,
    });
  };

  const announceImageIsOnItsWay = async (isSendingImage: boolean) => {
    try {
      await realtime?.sendDispatcherImagePreview(isSendingImage);
    } catch (error) {
      console.error('Announcing the image preview failed.', error);
    }
  };

  const sendImageMessage = async (attachment: AttachmentBody) => {
    dispatch({ type: 'send-image-started' });

    try {
      await announceImageIsOnItsWay(true);
      await realtime?.sendChatMessage({
        message: null,
        emergencySituationInstructions: EmergencySituationInstructions.None,
        attachment,
      });
    } finally {
      await announceImageIsOnItsWay(false);
      dispatch({ type: 'send-image-finished' });
    }
  };

  const sendMessagePreview = async (message: string) => {
    try {
      await realtime?.sendDispatcherTextPreview(message);
    } catch (error) {
      console.error('Sending the message preview failed.', error);
    }
  };

  return { sendMessage, sendImageMessage, sendMessagePreview };
}
